"use client";

import { useEffect } from "react";

/**
 * The last boundary: this one catches a throw in the ROOT layout itself.
 *
 * error.tsx cannot, because it renders inside that layout — if the layout is
 * what failed, there is nothing left to render into. Next replaces the whole
 * document in that case, which is why this file must supply its own <html> and
 * <body>.
 *
 * Every style here is inline, on purpose. Bypassing the root layout also
 * bypasses the `import "./globals.css"` it carries, so a Tailwind class name in
 * this file would be a class that does not exist, and the one screen that shows
 * up when everything else is broken would render as unstyled black-on-white
 * with no colour and no layout. Inline styles cannot fail that way. It is the
 * one file in the app where matching the house style is worth less than being
 * certain it renders at all.
 *
 * The hex values are the real tokens copied out of globals.css (--bg, --text,
 * --bad, --muted2, --surface2, --border), not approximations. --muted2 is the
 * body-copy step on purpose: globals.css marks --muted "LARGE TEXT ONLY ...
 * never an error message". Keep them in sync by hand; there is no other way
 * here, and drifting a shade is a far smaller failure than not rendering.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[panel] ROOT LAYOUT render error", {
      digest: error.digest ?? null,
      message: error.message,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          background: "#080a0f",
          color: "#eceef4",
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: "640px", width: "100%" }}>
          <h1 style={{ fontSize: "18px", fontWeight: 700, color: "#ef4444", margin: "0 0 12px" }}>
            The panel failed to start rendering
          </h1>
          <p style={{ fontSize: "14px", lineHeight: 1.6, color: "#8896b3", margin: "0 0 16px" }}>
            This is the root layout failing, not one screen — every page is affected. The app
            process itself may still be healthy, so <code>/api/health</code> can still say ok. Check{" "}
            <code>pm2 logs panel-web --lines 50 --nostream</code> on the server and grep the digest
            below.
          </p>
          <pre
            style={{
              background: "#141920",
              border: "1px solid #1e2533",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "12px",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              color: "#8896b3",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              margin: "0 0 16px",
            }}
          >
            digest: {error.digest ?? "(none)"}
            {"\n"}
            {error.message}
          </pre>
          <button
            type="button"
            onClick={reset}
            style={{
              background: "transparent",
              border: "1px solid #1e2533",
              borderRadius: "8px",
              color: "#8896b3",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 500,
              padding: "6px 12px",
            }}
          >
            try again
          </button>
        </div>
      </body>
    </html>
  );
}
